import { useEffect, useMemo, useState } from "react";
import { useQueries, useQuery } from "@tanstack/react-query";
import {
  Card,
  EmptyState,
  Pill,
  ProgressBar,
  SectionHeader,
  StatCard,
  Tab,
  Tabs,
} from "../components/design-system/index.js";
import {
  getBenchmark,
  getConcentration,
  getExpenseRatios,
  getOverlap,
  getSectorExposure,
  getTaxCost,
  type BenchmarkKey,
  type BenchmarkResponse,
  type ConcentrationResponse,
  type ConcentrationSeverity,
  type ExpenseRatiosResponse,
  type OverlapResponse,
  type SectorExposureResponse,
  type TaxCostResponse,
} from "../lib/api-modules/portfolio.js";
import { listAccounts } from "../lib/api-modules/accounts.js";
import { NetWorthBridge } from "../components/accounts/NetWorthBridge.js";
import {
  getIndexHistory,
  getNetWorth,
  type IndexHistoryResponse,
} from "../lib/api-modules/networth.js";
import { useAuth } from "../lib/auth-context.js";
import { usePreferences } from "../lib/preferences.js";
import { overlayColor } from "../lib/util/chart-theme.js";
import {
  PortfolioPerformanceChart,
  type PerformanceChartAccount,
  type PerformanceChartOverlay,
} from "../components/portfolio/PortfolioPerformanceChart.js";

// Phase 16d (2026-05-10): range pills mirror the dashboard chart's
// `1Y / 3Y / 5Y / All` semantics. The handler caps at 3650; "All" maps
// to 3650 so we get the most-recent ~10 years of snapshots, which is
// the practical horizon for `net_worth_snapshots` post-backfill.
type BenchmarkRange = "1y" | "3y" | "5y" | "all";
const BENCHMARK_RANGE_DAYS: Record<BenchmarkRange, number> = {
  "1y": 365,
  "3y": 1095,
  "5y": 1825,
  all: 3650,
};
import { formatCentsCurrency } from "../lib/util/money.js";
import { SuggestedPromptsTile } from "../components/SuggestedPromptsTile.js";
import { PAGE_PROMPTS } from "../lib/data/page-prompts.js";

/**
 * /portfolio — Phase 11 Cleave 1.
 *
 * Three tabs in this cleave:
 *   1. Concentration — per-ticker pct + severity flag.
 *   2. Overlap       — direct + indirect (look-through) exposure.
 *   3. Sectors       — GICS sector + region rollup.
 *
 * Subsequent cleaves add Tax cost / Benchmarks tabs (Cleave 2);
 * Rebalance + Stress test live as their own sub-pages (Cleave 3).
 *
 * Both themes (light + dark) supported per the App Shell interstitial.
 */

type ActiveTab =
  | "concentration"
  | "overlap"
  | "sectors"
  | "expense-ratios"
  | "tax-cost"
  | "benchmark"
  | "performance";

function PortfolioBridge() {
  const { user } = useAuth();
  const accountsQuery = useQuery({
    queryKey: ["accounts-summary"],
    queryFn: () => listAccounts(),
    enabled: !!user,
    staleTime: 60_000,
  });
  if (!accountsQuery.data) return null;
  return <NetWorthBridge totals={accountsQuery.data.totals} />;
}

export function PortfolioPage() {
  const [tab, setTab] = useState<ActiveTab>("concentration");
  return (
    <div className="mx-auto max-w-6xl space-y-6 px-4 py-8 sm:px-6">
      <SectionHeader
        title="Portfolio"
        sub="Your investments only. Here's how they add up to your net worth."
      />
      <PortfolioBridge />
      <Tabs
        value={tab}
        onChange={(v) => setTab(v as ActiveTab)}
        ariaLabel="Portfolio sections"
        // Phase 13.5 — Rebalance + Stress test were removed from the
        // WEALTH sidebar group (IA review 2026-05-09) and surfaced here
        // as trailing link-tabs in the same strip as the in-page tabs.
        // Their routes survive at /portfolio/rebalance + /portfolio/stress-test.
        trailingLinks={[
          { to: "/portfolio/rebalance", label: "Rebalance" },
          { to: "/portfolio/stress-test", label: "Stress test" },
        ]}
      >
        <Tab value="concentration" label="Concentration">
          <ConcentrationTab />
        </Tab>
        <Tab value="overlap" label="Overlap">
          <OverlapTab />
        </Tab>
        <Tab value="sectors" label="Sectors">
          <SectorsTab />
        </Tab>
        <Tab value="expense-ratios" label="Expense ratios">
          <ExpenseRatiosTab />
        </Tab>
        <Tab value="tax-cost" label="Tax cost">
          <TaxCostTab />
        </Tab>
        <Tab value="benchmark" label="Benchmark">
          <BenchmarkTab />
        </Tab>
        <Tab value="performance" label="Performance">
          <PerformanceTab />
        </Tab>
      </Tabs>
      <div className="mt-8">
        <SuggestedPromptsTile prompts={PAGE_PROMPTS.portfolio} />
      </div>
    </div>
  );
}

// ─── Expense ratios tab ───────────────────────────────────────────────────

function ExpenseRatiosTab() {
  const { data, isLoading, error } = useQuery<ExpenseRatiosResponse>({
    queryKey: ["portfolio", "expense-ratios"],
    queryFn: getExpenseRatios,
  });
  if (isLoading) return <p className="text-sm text-[var(--color-muted)]">Loading…</p>;
  if (error) return <ErrorMessage error={error} />;
  if (!data) return null;
  if (data.rows.length === 0) {
    return (
      <EmptyState
        title="No fund holdings"
        description="Direct equities don't carry expense ratios. Once you hold an ETF or mutual fund, this tab shows annual drag and switching opportunities."
      />
    );
  }
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <StatCard
          label="Total fund AUM"
          value={formatCentsCurrency(data.total_value_cents)}
        />
        <StatCard
          label="Annual drag"
          value={formatCentsCurrency(data.total_annual_drag_cents)}
          sub={`${data.weighted_avg_expense_ratio_bp.toFixed(1)} bp weighted ER`}
        />
        <StatCard
          label="Potential annual savings"
          value={formatCentsCurrency(data.total_potential_annual_savings_cents)}
          sub="If you switched to recommended ETF substitutes"
        />
      </div>
      <Card>
        <p className="mb-3 text-xs text-[var(--color-muted)]">
          {data.rule_of_thumb} Data as of {data.data_as_of}.
        </p>
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase tracking-wide text-[var(--color-muted)]">
            <tr>
              <th className="py-2">Ticker</th>
              <th className="py-2 text-right">Value</th>
              <th className="py-2 text-right">ER (bp)</th>
              <th className="py-2 text-right">Annual drag</th>
              <th className="py-2">Switch?</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[var(--color-border-subtle)]">
            {data.rows.map((row) => (
              <tr key={row.ticker}>
                <td className="py-2 font-mono font-medium text-[var(--color-text)]">{row.ticker}</td>
                <td className="py-2 text-right tabular-nums text-[var(--color-muted)]">
                  {formatCentsCurrency(row.value_cents)}
                </td>
                <td className="py-2 text-right tabular-nums text-[var(--color-muted)]">
                  {row.expense_ratio_bp}
                </td>
                <td className="py-2 text-right tabular-nums text-[var(--color-text)]">
                  {formatCentsCurrency(row.est_annual_drag_cents)}
                </td>
                <td className="py-2 text-[var(--color-muted)]">
                  {row.recommended_etf_equivalent ? (
                    <Pill tone="accent">
                      → {row.recommended_etf_equivalent} (saves{" "}
                      {formatCentsCurrency(row.est_annual_savings_if_switched_cents ?? 0)}/yr)
                    </Pill>
                  ) : (
                    <span className="text-xs">—</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {data.unknown_tickers.length > 0 && (
          <p className="mt-2 text-xs text-[var(--color-muted)]">
            {data.unknown_tickers.length} direct-equity tickers excluded (no expense ratio applies).
          </p>
        )}
      </Card>
    </div>
  );
}

// ─── Tax cost tab ─────────────────────────────────────────────────────────

function TaxCostTab() {
  const { data, isLoading, error } = useQuery<TaxCostResponse>({
    queryKey: ["portfolio", "tax-cost"],
    queryFn: getTaxCost,
  });
  if (isLoading) return <p className="text-sm text-[var(--color-muted)]">Loading…</p>;
  if (error) return <ErrorMessage error={error} />;
  if (!data) return null;
  if (data.rows.length === 0) {
    return (
      <EmptyState
        title="No fund holdings"
        description="Direct equities have no fund distributions. This tab measures distribution-driven tax drag from ETFs and mutual funds in TAXABLE accounts."
      />
    );
  }
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <StatCard
          label="Total fund AUM"
          value={formatCentsCurrency(data.total_value_cents)}
        />
        <StatCard
          label="Annual tax drag"
          value={formatCentsCurrency(data.total_annual_tax_drag_cents)}
          sub={`${data.weighted_avg_tax_cost_ratio_bp.toFixed(1)} bp weighted TCR`}
        />
        <StatCard
          label="Potential annual savings"
          value={formatCentsCurrency(data.total_potential_annual_savings_cents)}
          sub="If you switched taxable mutual funds to ETF equivalents"
        />
      </div>
      <Card>
        <p className="mb-3 text-xs text-[var(--color-muted)]">
          {data.rule_of_thumb} Data as of {data.data_as_of}.
        </p>
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase tracking-wide text-[var(--color-muted)]">
            <tr>
              <th className="py-2">Ticker</th>
              <th className="py-2">Class</th>
              <th className="py-2 text-right">Value</th>
              <th className="py-2 text-right">Tax drag</th>
              <th className="py-2 text-right">TCR (bp)</th>
              <th className="py-2">Switch?</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[var(--color-border-subtle)]">
            {data.rows.map((row) => (
              <tr key={row.ticker}>
                <td className="py-2 font-mono font-medium text-[var(--color-text)]">
                  {row.ticker}
                  {!row.is_taxable_account && (
                    <Pill tone="success" className="ml-2">
                      tax-deferred
                    </Pill>
                  )}
                </td>
                <td className="py-2 text-xs text-[var(--color-muted)]">
                  {row.tax_efficiency_class.replace(/_/g, " ")}
                </td>
                <td className="py-2 text-right tabular-nums text-[var(--color-muted)]">
                  {formatCentsCurrency(row.value_cents)}
                </td>
                <td className="py-2 text-right tabular-nums text-[var(--color-text)]">
                  {formatCentsCurrency(row.est_annual_tax_drag_cents)}
                </td>
                <td className="py-2 text-right tabular-nums text-[var(--color-muted)]">
                  {row.est_annual_tax_cost_ratio_bp.toFixed(1)}
                </td>
                <td className="py-2">
                  {row.recommended_etf_equivalent ? (
                    <Pill tone="accent">
                      → {row.recommended_etf_equivalent}
                    </Pill>
                  ) : (
                    <span className="text-xs text-[var(--color-muted)]">—</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

// ─── Benchmark tab ────────────────────────────────────────────────────────

function BenchmarkTab() {
  const [vs, setVs] = useState<BenchmarkKey>("sp500");
  const [range, setRange] = useState<BenchmarkRange>("1y");
  const periodDays = BENCHMARK_RANGE_DAYS[range];

  // Phase 16d: actual-ETF Modified Dietz is the new primary
  // (apples-to-apples nominal-vs-nominal); historical-real is the
  // informational long-window context card. Both fetched in parallel
  // so the tab renders both side-by-side without sequential waterfall.
  const primaryQuery = useQuery<BenchmarkResponse>({
    queryKey: ["portfolio", "benchmark", "actual_etf", vs, periodDays],
    queryFn: () =>
      getBenchmark({ vs, period_days: periodDays, kind: "actual_etf" }),
  });
  const historicalQuery = useQuery<BenchmarkResponse>({
    queryKey: ["portfolio", "benchmark", "historical_real", vs, periodDays],
    queryFn: () =>
      getBenchmark({ vs, period_days: periodDays, kind: "historical_real" }),
  });

  if (primaryQuery.isLoading)
    return <p className="text-sm text-[var(--color-muted)]">Loading…</p>;
  if (primaryQuery.error) return <ErrorMessage error={primaryQuery.error} />;
  const data = primaryQuery.data;
  if (!data) return null;
  if (data.error_code === "INSUFFICIENT_SNAPSHOTS") {
    return (
      <EmptyState
        title="Not enough net-worth history yet"
        description={
          data.message ??
          "The nightly snapshot job needs to run for at least the start of the comparison window."
        }
      />
    );
  }

  // sixty_forty / custom_pct fall back to historical_real on the
  // server (no multi-ticker blend in v1); reflect that in the label.
  const effectiveKind = data.benchmark_kind ?? "actual_etf";
  const usingFallback =
    effectiveKind === "historical_real" && (vs === "sixty_forty" || vs === "custom_pct");

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        {(
          ["sp500", "total_us_market", "total_world", "sixty_forty"] as BenchmarkKey[]
        ).map((b) => (
          <button
            key={b}
            type="button"
            onClick={() => setVs(b)}
            className={
              b === vs
                ? "rounded-full bg-[var(--color-accent)] px-3 py-1 text-xs font-medium text-[var(--color-accent-contrast)]"
                : "rounded-full border border-[var(--color-border-default)] px-3 py-1 text-xs text-[var(--color-text)] hover:bg-[var(--color-elevated)]"
            }
          >
            {b.replace(/_/g, " ")}
          </button>
        ))}
        <span className="mx-2 h-4 w-px bg-[var(--color-border-default)]" />
        {(["1y", "3y", "5y", "all"] as BenchmarkRange[]).map((r) => (
          <button
            key={r}
            type="button"
            onClick={() => setRange(r)}
            className={
              r === range
                ? "rounded-md bg-[var(--color-accent-soft)] px-3 py-1 text-xs font-medium text-[var(--color-accent)]"
                : "rounded-md px-3 py-1 text-xs text-[var(--color-muted)] hover:bg-[var(--color-elevated)] hover:text-[var(--color-text)]"
            }
            data-testid={`benchmark-range-${r}`}
          >
            {r.toUpperCase()}
          </button>
        ))}
      </div>

      {/* Primary card — actual ETF nominal-vs-nominal Modified Dietz. */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <StatCard
          label="Your annualized return"
          value={`${(data.portfolio.annualized_pct * 100).toFixed(2)}%`}
          sub={`Modified Dietz over ${data.period_days} days`}
        />
        <StatCard
          label={
            usingFallback
              ? `${vs.replace(/_/g, " ")} (historical real)`
              : `${data.etf_ticker ?? vs.toUpperCase()} (annualized)`
          }
          value={`${(data.benchmark_annualized_pct * 100).toFixed(2)}%`}
          sub={
            usingFallback
              ? "Multi-ticker blend uses the historical-real dataset in v1"
              : `Actual ${data.etf_ticker ?? vs.toUpperCase()} adj_close — same window, nominal`
          }
        />
        <StatCard
          label="Excess vs benchmark"
          value={`${data.excess_pct >= 0 ? "+" : ""}${(data.excess_pct * 100).toFixed(2)}%`}
          sub={
            data.excess_cents_at_end
              ? `${formatCentsCurrency(data.excess_cents_at_end.amount_cents)} at end vs counter-factual`
              : undefined
          }
          trendDirection={data.excess_pct >= 0 ? "up" : "down"}
        />
      </div>

      {/* Informational card — historical-real long-window context. */}
      {historicalQuery.data &&
        !historicalQuery.data.error_code &&
        !usingFallback && (
          <Card>
            <p className="text-xs font-medium uppercase tracking-wide text-[var(--color-muted)]">
              Long-term historical context
            </p>
            <p className="mt-2 text-sm text-[var(--color-text)]">
              {(historicalQuery.data.benchmark_annualized_pct * 100).toFixed(2)}%
              <span className="ml-2 text-xs text-[var(--color-muted)]">
                — geomean of the {vs.replace(/_/g, " ")} component of the
                historical-real dataset (1928–latest, real, inflation-adjusted)
              </span>
            </p>
            <p className="mt-1 text-xs text-[var(--color-muted)]">
              Real (inflation-adjusted) returns aren't directly comparable to
              your nominal Modified Dietz above; this is historical-context
              only, not a like-to-like benchmark.
            </p>
          </Card>
        )}

      <Card>
        <p className="text-xs text-[var(--color-muted)]">{data.methodology}</p>
        <p className="mt-2 text-xs text-[var(--color-muted)]">
          {data.cashflows_attributed} cashflow event(s) attributed (deposits,
          withdrawals, contributions, transfers from the transactions table).
          Internal flows (buy / sell / dividend / interest / fee /
          drip_purchase) net inside the start/end snapshot values.
        </p>
      </Card>
    </div>
  );
}

// ─── Performance tab (Phase 16e — 2026-05-10) ────────────────────────────
//
// Portfolio + per-account performance over a selected window. Multi-
// series normalized chart (lines, every series anchored to 100 at its
// first non-zero value in the window) + per-account return-metric card
// grid (TWR / IRR / Max DD / excess vs SPY). Click a card → highlight
// that account's line on the chart. Index overlays SPY/QQQ/VT.
//
// Distinct from Benchmark tab (Modified Dietz cards) and from
// Concentration / Tax cost / Overlap / Sectors (per-holding analyses).
// Performance is "did the equity portion actually beat the benchmark,
// per-account?" — the answer that drives contribution prioritization.

const PERF_RANGE_DAYS: Record<"1y" | "3y" | "5y" | "all", number> = {
  "1y": 365,
  "3y": 1095,
  "5y": 1825,
  all: 3650,
};
type PerfRange = keyof typeof PERF_RANGE_DAYS;

const PERF_RANGE_TO_NETWORTH_WINDOW: Record<PerfRange, "24m" | "5y" | "all"> = {
  "1y": "24m",
  "3y": "5y",
  "5y": "5y",
  all: "all",
};

const PERF_INDEX_TICKERS = ["SPY", "QQQ", "VT"] as const;

const PERF_ACCOUNTS_STORAGE_PREFIX = "hearth:portfolio-performance:accounts";

function readSelectedAccounts(householdId: string | undefined): Set<string> | null {
  if (!householdId || typeof globalThis === "undefined" || !globalThis.localStorage) {
    return null;
  }
  try {
    const raw = globalThis.localStorage.getItem(
      `${PERF_ACCOUNTS_STORAGE_PREFIX}:${householdId}`,
    );
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return null;
    return new Set(parsed.filter((x): x is string => typeof x === "string"));
  } catch {
    return null;
  }
}

function persistSelectedAccounts(householdId: string, ids: ReadonlySet<string>) {
  if (typeof globalThis === "undefined" || !globalThis.localStorage) return;
  try {
    globalThis.localStorage.setItem(
      `${PERF_ACCOUNTS_STORAGE_PREFIX}:${householdId}`,
      JSON.stringify([...ids]),
    );
  } catch {
    // Storage quota exceeded or disabled; keep the in-memory state.
  }
}

function PerformanceTab() {
  const { user } = useAuth();
  const { theme, privacyMode } = usePreferences();
  const [range, setRange] = useState<PerfRange>("3y");
  const [selectedOverlays, setSelectedOverlays] = useState<Set<string>>(new Set());
  const [selectedAccounts, setSelectedAccounts] = useState<Set<string>>(new Set());
  const [highlightedAccountId, setHighlightedAccountId] = useState<string | null>(null);
  const [accountSelectionInitialized, setAccountSelectionInitialized] = useState(false);

  const networthQuery = useQuery({
    queryKey: ["networth", PERF_RANGE_TO_NETWORTH_WINDOW[range]],
    queryFn: () => getNetWorth(PERF_RANGE_TO_NETWORTH_WINDOW[range]),
    enabled: !!user,
  });
  const accountsQuery = useQuery({
    queryKey: ["accounts-summary"],
    queryFn: () => listAccounts(),
    enabled: !!user,
    staleTime: 60_000,
  });

  // Trim snapshots to the requested PERF_RANGE_DAYS (the underlying
  // /v1/networth windows are coarser — 24m/5y/all). Recharts handles
  // the trimming visually but the metric cards need an aligned period.
  const snapshots = useMemo(() => {
    const all = networthQuery.data?.snapshots ?? [];
    if (all.length === 0) return all;
    if (range === "all") return all;
    const days = PERF_RANGE_DAYS[range];
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - days);
    const cutoffIso = cutoff.toISOString().slice(0, 10);
    return all.filter((s) => s.date >= cutoffIso);
  }, [networthQuery.data, range]);

  // Available accounts = those that appear in any snapshot's
  // portfolio_by_account (cash + liability accounts have no entry).
  const availableAccounts = useMemo<PerformanceChartAccount[]>(() => {
    const accountList = accountsQuery.data?.accounts ?? [];
    const seen = new Set<string>();
    for (const s of snapshots) {
      for (const id of Object.keys(s.portfolio_by_account ?? {})) seen.add(id);
    }
    return accountList
      .filter((a) => seen.has(a.account_id) && !a.is_archived)
      .sort((a, b) => b.total_value_cents - a.total_value_cents)
      .map((a, i) => ({
        account_id: a.account_id,
        name: a.name,
        color: overlayColor(i, theme === "dark" ? "dark" : "light"),
      }));
  }, [accountsQuery.data, snapshots, theme]);

  // Hydrate the selected-account set from localStorage on first paint
  // for the household; default to all available accounts if no prior
  // selection exists. Keyed per household so multi-tenant logins don't
  // bleed selection state across users.
  const householdId = user?.primary_household_id;
  useEffect(() => {
    if (accountSelectionInitialized) return;
    if (availableAccounts.length === 0) return;
    const stored = readSelectedAccounts(householdId);
    const next = stored
      ? new Set([...stored].filter((id) => availableAccounts.some((a) => a.account_id === id)))
      : new Set(availableAccounts.map((a) => a.account_id));
    setSelectedAccounts(next);
    setAccountSelectionInitialized(true);
  }, [availableAccounts, householdId, accountSelectionInitialized]);

  const toggleAccount = (accountId: string) => {
    setSelectedAccounts((prev) => {
      const next = new Set(prev);
      if (next.has(accountId)) next.delete(accountId);
      else next.add(accountId);
      if (householdId) persistSelectedAccounts(householdId, next);
      return next;
    });
  };

  const toggleAllAccounts = () => {
    setSelectedAccounts((prev) => {
      const next =
        prev.size === availableAccounts.length
          ? new Set<string>()
          : new Set(availableAccounts.map((a) => a.account_id));
      if (householdId) persistSelectedAccounts(householdId, next);
      return next;
    });
  };

  const toggleOverlay = (ticker: string) => {
    setSelectedOverlays((prev) => {
      const next = new Set(prev);
      if (next.has(ticker)) next.delete(ticker);
      else next.add(ticker);
      return next;
    });
  };

  const visibleAccounts = useMemo(
    () =>
      availableAccounts.filter((a) => selectedAccounts.has(a.account_id)),
    [availableAccounts, selectedAccounts],
  );

  // Index-overlay queries — one per selected ticker. Reuse the 16d
  // shape used by /dashboard.
  const startDate = snapshots[0]?.date;
  const endDate = snapshots[snapshots.length - 1]?.date;
  const overlayTickers = useMemo(
    () => Array.from(selectedOverlays),
    [selectedOverlays],
  );
  const overlayQuery = useQuery<IndexHistoryResponse[]>({
    queryKey: ["perf-index-history", overlayTickers.join(","), startDate, endDate],
    queryFn: async () => {
      if (!startDate || !endDate || overlayTickers.length === 0) return [];
      return Promise.all(
        overlayTickers.map((t) => getIndexHistory(t, startDate, endDate)),
      );
    },
    enabled: overlayTickers.length > 0 && !!startDate && !!endDate,
    staleTime: 24 * 60 * 60 * 1000,
  });

  const overlaySeries = useMemo<PerformanceChartOverlay[]>(() => {
    if (!overlayQuery.data) return [];
    return overlayQuery.data.map((resp, i) => ({
      key: resp.ticker,
      label: resp.ticker,
      // Overlay slot starts after the per-account palette so the colors
      // don't visually collide with the per-account lines.
      color: overlayColor(
        availableAccounts.length + i,
        theme === "dark" ? "dark" : "light",
      ),
      bars: resp.bars.map((b) => ({
        date: b.date,
        close_cents: Math.round(b.close * 100),
      })),
    }));
  }, [overlayQuery.data, availableAccounts.length, theme]);

  // Per-account benchmark + metric cards — N parallel /v1/portfolio/
  // benchmark calls scoped via &account_id=. ~6-7 accounts; comfortable
  // in parallel. Cached on (account_id, period_days).
  const periodDays = PERF_RANGE_DAYS[range];
  const cardQueries = useQueries({
    queries: visibleAccounts.map((acct) => ({
      queryKey: ["portfolio-perf-card", acct.account_id, periodDays] as const,
      queryFn: () =>
        getBenchmark({
          vs: "sp500",
          period_days: periodDays,
          account_id: acct.account_id,
        }),
      enabled: !!user,
      staleTime: 5 * 60 * 1000,
    })),
  });

  if (networthQuery.isLoading || accountsQuery.isLoading) {
    return <p className="text-sm text-[var(--color-muted)]">Loading…</p>;
  }
  if (networthQuery.error)
    return <ErrorMessage error={networthQuery.error} />;
  if (accountsQuery.error)
    return <ErrorMessage error={accountsQuery.error} />;
  if (snapshots.length < 2) {
    return (
      <EmptyState
        title="Not enough portfolio history yet"
        description="The /portfolio Performance tab needs at least two days of net-worth snapshots populated with portfolio_value_cents to render. Run pnpm jobs:invoke-networth-backfill or wait for two nightly snapshots."
      />
    );
  }

  return (
    <div className="space-y-5">
      {/* Range pills + index-overlay pills */}
      <div className="flex flex-wrap items-center gap-2">
        {(["1y", "3y", "5y", "all"] as PerfRange[]).map((r) => (
          <button
            key={r}
            type="button"
            onClick={() => setRange(r)}
            className={
              r === range
                ? "rounded-md bg-[var(--color-accent-soft)] px-3 py-1 text-xs font-medium text-[var(--color-accent)]"
                : "rounded-md px-3 py-1 text-xs text-[var(--color-muted)] hover:bg-[var(--color-elevated)] hover:text-[var(--color-text)]"
            }
            data-testid={`perf-range-${r}`}
          >
            {r.toUpperCase()}
          </button>
        ))}
        <span className="mx-2 h-4 w-px bg-[var(--color-border-default)]" />
        {PERF_INDEX_TICKERS.map((t, i) => {
          const active = selectedOverlays.has(t);
          const color = overlayColor(
            availableAccounts.length + i,
            theme === "dark" ? "dark" : "light",
          );
          return (
            <button
              key={t}
              type="button"
              onClick={() => toggleOverlay(t)}
              style={active ? { borderColor: color, color } : undefined}
              className={
                active
                  ? "rounded-full border px-3 py-1 text-xs font-medium"
                  : "rounded-full border border-[var(--color-border-default)] px-3 py-1 text-xs text-[var(--color-muted)] hover:text-[var(--color-text)]"
              }
            >
              {active ? "− " : "+ "}
              {t}
            </button>
          );
        })}
      </div>

      {/* Account checkboxes */}
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={toggleAllAccounts}
          className="rounded-full border border-[var(--color-border-default)] px-3 py-1 text-xs text-[var(--color-muted)] hover:text-[var(--color-text)]"
        >
          {selectedAccounts.size === availableAccounts.length ? "Hide all" : "Show all"}
        </button>
        {availableAccounts.map((acct) => {
          const checked = selectedAccounts.has(acct.account_id);
          return (
            <label
              key={acct.account_id}
              className={
                checked
                  ? "inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-medium"
                  : "inline-flex items-center gap-2 rounded-full border border-[var(--color-border-default)] px-3 py-1 text-xs text-[var(--color-muted)] hover:text-[var(--color-text)]"
              }
              style={checked ? { borderColor: acct.color, color: acct.color } : undefined}
            >
              <input
                type="checkbox"
                checked={checked}
                onChange={() => toggleAccount(acct.account_id)}
                className="h-3 w-3 rounded"
              />
              {acct.name}
            </label>
          );
        })}
      </div>

      <Card>
        <PortfolioPerformanceChart
          snapshots={snapshots}
          accounts={visibleAccounts}
          overlays={overlaySeries}
          highlightedAccountId={highlightedAccountId}
          hideValues={privacyMode}
        />
        <p className="mt-3 text-xs text-[var(--color-muted)]">
          Every series normalized to 100 at the start of the window (or
          the account's first positions-bearing day if it opened later).
          Positions-only — cash sleeves, margin, and liabilities are
          excluded so the comparison reflects equity performance honestly.
        </p>
      </Card>

      {/* Per-account return-metric card grid */}
      {visibleAccounts.length > 0 && (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {visibleAccounts.map((acct, i) => {
            const q = cardQueries[i];
            return (
              <PerformanceCard
                key={acct.account_id}
                account={acct}
                response={q?.data}
                isLoading={q?.isLoading ?? false}
                error={q?.error ?? null}
                highlighted={highlightedAccountId === acct.account_id}
                onMouseEnter={() => setHighlightedAccountId(acct.account_id)}
                onMouseLeave={() => setHighlightedAccountId(null)}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

function PerformanceCard({
  account,
  response,
  isLoading,
  error,
  highlighted,
  onMouseEnter,
  onMouseLeave,
}: {
  account: PerformanceChartAccount;
  response: BenchmarkResponse | undefined;
  isLoading: boolean;
  error: unknown;
  highlighted: boolean;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
}) {
  const containerClass = highlighted
    ? "rounded-lg border-2 bg-[var(--color-card)] p-4 shadow-[var(--shadow-card)] transition-all"
    : "rounded-lg border border-[var(--color-border-subtle)] bg-[var(--color-card)] p-4 transition-all hover:border-[var(--color-border-default)]";
  return (
    <div
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      style={highlighted ? { borderColor: account.color } : undefined}
      className={containerClass}
      data-testid={`performance-card-${account.account_id}`}
    >
      <div className="flex items-baseline justify-between gap-3">
        <span
          className="text-sm font-medium text-[var(--color-text)]"
          style={{ color: account.color }}
        >
          {account.name}
        </span>
      </div>
      {isLoading ? (
        <p className="mt-3 text-xs text-[var(--color-muted)]">Computing…</p>
      ) : error ? (
        <p className="mt-3 text-xs text-[var(--color-danger)]">
          {error instanceof Error ? error.message : "Failed to load"}
        </p>
      ) : !response || response.error_code === "INSUFFICIENT_SNAPSHOTS" ? (
        <p className="mt-3 text-xs text-[var(--color-muted)]">
          {response?.message ??
            "Not enough portfolio history for this account in the window."}
        </p>
      ) : (
        <ul className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
          <MetricRow
            label="TWR"
            value={response.twr_pct !== undefined ? pct(response.twr_pct) : "—"}
          />
          <MetricRow
            label="IRR"
            value={
              response.irr_pct === null || response.irr_pct === undefined
                ? "—"
                : pct(response.irr_pct)
            }
          />
          <MetricRow
            label="Max DD"
            value={
              response.max_drawdown_pct !== undefined
                ? pct(response.max_drawdown_pct)
                : "—"
            }
            tone="negative"
          />
          <MetricRow
            label="vs SPY"
            value={pct(response.excess_pct)}
            tone={response.excess_pct >= 0 ? "positive" : "negative"}
          />
        </ul>
      )}
    </div>
  );
}

function MetricRow({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: "positive" | "negative";
}) {
  const valueClass =
    tone === "positive"
      ? "font-mono text-sm tabular-nums text-[var(--color-success)]"
      : tone === "negative"
        ? "font-mono text-sm tabular-nums text-[var(--color-danger)]"
        : "font-mono text-sm tabular-nums text-[var(--color-text)]";
  return (
    <li className="flex items-center justify-between gap-2">
      <span className="text-[var(--color-muted)]">{label}</span>
      <span className={valueClass}>{value}</span>
    </li>
  );
}

function pct(v: number): string {
  return `${v >= 0 ? "+" : ""}${(v * 100).toFixed(2)}%`;
}

// ─── Concentration tab ────────────────────────────────────────────────────

function ConcentrationTab() {
  const { data, isLoading, error } = useQuery<ConcentrationResponse>({
    queryKey: ["portfolio", "concentration"],
    queryFn: getConcentration,
  });

  if (isLoading) return <p className="text-sm text-[var(--color-muted)]">Loading…</p>;
  if (error) return <ErrorMessage error={error} />;
  if (!data) return null;
  if (data.rows.length === 0) {
    return (
      <EmptyState
        title="No holdings on file"
        description="Add holdings on the Accounts page to see concentration analysis."
      />
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <StatCard
          label="Total portfolio"
          value={formatCentsCurrency(data.total_value_cents)}
        />
        <StatCard
          label="Warning positions"
          value={data.warning_count.toString()}
          sub={`≥${data.warn_pct.toFixed(0)}% of portfolio`}
        />
        <StatCard
          label="Critical positions"
          value={data.critical_count.toString()}
          sub={`≥${(data.warn_pct * 1.5).toFixed(1)}% of portfolio`}
        />
      </div>
      <Card>
        <p className="mb-3 text-xs text-[var(--color-muted)]">{data.rule_of_thumb}</p>
        <ul className="divide-y divide-[var(--color-border-subtle)]">
          {data.rows.map((row) => (
            <li
              key={row.ticker}
              className="grid grid-cols-[1fr_auto_120px_auto] items-center gap-3 py-2"
            >
              <span className="font-mono text-sm font-medium text-[var(--color-text)]">
                {row.ticker}
              </span>
              <span className="text-sm tabular-nums text-[var(--color-muted)]">
                {formatCentsCurrency(row.value_cents)}
              </span>
              <ProgressBar
                value={row.pct_of_portfolio}
                max={100}
                tone={progressBarTone(row.severity)}
                ariaLabel={`${row.ticker} ${row.pct_of_portfolio.toFixed(1)}%`}
              />
              <div className="flex items-center justify-end gap-2">
                <span className="font-mono text-xs tabular-nums text-[var(--color-muted)]">
                  {row.pct_of_portfolio.toFixed(1)}%
                </span>
                <SeverityPill severity={row.severity} />
              </div>
            </li>
          ))}
        </ul>
      </Card>
    </div>
  );
}

// ─── Overlap tab ──────────────────────────────────────────────────────────

function OverlapTab() {
  const { data, isLoading, error } = useQuery<OverlapResponse>({
    queryKey: ["portfolio", "overlap"],
    queryFn: getOverlap,
  });

  if (isLoading) return <p className="text-sm text-[var(--color-muted)]">Loading…</p>;
  if (error) return <ErrorMessage error={error} />;
  if (!data) return null;
  if (data.rows.length === 0) {
    return (
      <EmptyState
        title="No look-through results"
        description="Add holdings (especially funds like VOO, VTI, VXUS) to see effective exposure."
      />
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <StatCard
          label="Total portfolio"
          value={formatCentsCurrency(data.total_value_cents)}
        />
        <StatCard
          label="Diversified residual via funds"
          value={formatCentsCurrency(data.diversified_residual_cents)}
          sub="Long-tail fund constituents we don't track"
        />
        <StatCard
          label="Held both ways"
          value={data.overlap_tickers.length.toString()}
          sub="Direct + via funds"
        />
      </div>
      <Card>
        <p className="mb-3 text-xs text-[var(--color-muted)]">
          {data.rule_of_thumb} Data as of {data.data_as_of}.
        </p>
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase tracking-wide text-[var(--color-muted)]">
            <tr>
              <th className="py-2">Ticker</th>
              <th className="py-2 text-right">Direct</th>
              <th className="py-2 text-right">Indirect</th>
              <th className="py-2 text-right">Effective</th>
              <th className="py-2 text-right">% of portfolio</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[var(--color-border-subtle)]">
            {data.rows.slice(0, 30).map((row) => (
              <tr key={row.ticker}>
                <td className="py-2 font-mono font-medium text-[var(--color-text)]">
                  {row.ticker}
                  {row.direct_value_cents > 0 && row.indirect_value_cents > 0 && (
                    <Pill tone="warning" className="ml-2">
                      overlap
                    </Pill>
                  )}
                </td>
                <td className="py-2 text-right tabular-nums text-[var(--color-muted)]">
                  {formatCentsCurrency(row.direct_value_cents)}
                </td>
                <td className="py-2 text-right tabular-nums text-[var(--color-muted)]">
                  {formatCentsCurrency(row.indirect_value_cents)}
                </td>
                <td className="py-2 text-right tabular-nums text-[var(--color-text)]">
                  {formatCentsCurrency(row.effective_value_cents)}
                </td>
                <td className="py-2 text-right tabular-nums text-[var(--color-text)]">
                  {row.effective_pct.toFixed(2)}%
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {data.rows.length > 30 && (
          <p className="mt-2 text-xs text-[var(--color-muted)]">
            Showing top 30 of {data.rows.length} positions.
          </p>
        )}
      </Card>
    </div>
  );
}

// ─── Sectors tab ──────────────────────────────────────────────────────────

function SectorsTab() {
  const { data, isLoading, error } = useQuery<SectorExposureResponse>({
    queryKey: ["portfolio", "sectors"],
    queryFn: getSectorExposure,
  });

  if (isLoading) return <p className="text-sm text-[var(--color-muted)]">Loading…</p>;
  if (error) return <ErrorMessage error={error} />;
  if (!data) return null;
  if (data.by_sector.length === 0) {
    return (
      <EmptyState
        title="No sector exposure"
        description="Add holdings to see sector and region rollups."
      />
    );
  }

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Card>
        <h3 className="mb-3 font-display text-lg font-medium text-[var(--color-text)]">
          By GICS sector
        </h3>
        <p className="mb-3 text-xs text-[var(--color-muted)]">
          {data.sector_taxonomy} · As of {data.data_as_of}
        </p>
        <ul className="space-y-3">
          {data.by_sector.map((row) => (
            <li key={row.key} className="space-y-1">
              <div className="flex items-baseline justify-between gap-2 text-sm">
                <span className="text-[var(--color-text)]">{row.key}</span>
                <span className="tabular-nums text-[var(--color-muted)]">
                  {formatCentsCurrency(row.value_cents)} · {row.pct.toFixed(1)}%
                </span>
              </div>
              <ProgressBar
                value={row.pct}
                max={100}
                tone="accent"
                ariaLabel={`${row.key} ${row.pct.toFixed(1)}%`}
              />
            </li>
          ))}
        </ul>
      </Card>
      <Card>
        <h3 className="mb-3 font-display text-lg font-medium text-[var(--color-text)]">
          By region
        </h3>
        <ul className="space-y-3">
          {data.by_region.map((row) => (
            <li key={row.key} className="space-y-1">
              <div className="flex items-baseline justify-between gap-2 text-sm">
                <span className="text-[var(--color-text)]">{row.key.replace(/_/g, " ")}</span>
                <span className="tabular-nums text-[var(--color-muted)]">
                  {formatCentsCurrency(row.value_cents)} · {row.pct.toFixed(1)}%
                </span>
              </div>
              <ProgressBar
                value={row.pct}
                max={100}
                tone="accent"
                ariaLabel={`${row.key} ${row.pct.toFixed(1)}%`}
              />
            </li>
          ))}
        </ul>
      </Card>
    </div>
  );
}

// ─── helpers ──────────────────────────────────────────────────────────────

function progressBarTone(severity: ConcentrationSeverity): "accent" | "warning" | "danger" {
  switch (severity) {
    case "warning":
      return "warning";
    case "critical":
      return "danger";
    default:
      return "accent";
  }
}

function SeverityPill({ severity }: { severity: ConcentrationSeverity }) {
  if (severity === "critical")
    return <Pill tone="danger">Critical</Pill>;
  if (severity === "warning") return <Pill tone="warning">Warning</Pill>;
  return <Pill tone="success">OK</Pill>;
}

function ErrorMessage({ error }: { error: unknown }) {
  const msg = error instanceof Error ? error.message : "Failed to load";
  return (
    <Card>
      <p className="text-sm text-[var(--color-danger)]">{msg}</p>
    </Card>
  );
}
